'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ImageGallery from './components/ImageGallery';
import Amenities from './components/Amenities';
import RoomsList from './components/RoomsList';
import AvailabilityCalendar from './components/AvailabilityCalendar';
import PricingBreakdown from './components/PricingBreakdown';
import HostInfo from './components/HostInfo';
import Reviews from './components/Reviews';
import BookingModal from '@/app/search/components/BookingModal';

// ─── Mock Data ────────────────────────────────────────────────────────────────

const property = {
  id: 1,
  name: 'Acacia Luxury Apartments',
  location: 'Kololo, Kampala',
  country: 'Uganda',
  type: 'Serviced Apartment',
  rating: 4.87,
  reviews: 124,
  pricePerNight: 180000,
  currency: 'UGX',
  cleaningFee: 25000,
  serviceFee: 18000,
  description: `Nestled in the serene hills of Kololo, Acacia Luxury Apartments offers an unparalleled blend of modern comfort and authentic Ugandan hospitality. Each apartment is thoughtfully designed with premium furnishings, floor-to-ceiling windows, and private balconies overlooking the lush Kampala skyline.

Perfect for business travellers and leisure guests alike, our serviced apartments provide the space and privacy of a home with the amenities of a five-star hotel. Wake up to panoramic city views, enjoy a morning swim in our rooftop pool, and let our dedicated concierge team curate your perfect Kampala experience.`,
  maxGuests: 4,
  bedrooms: 2,
  bathrooms: 2,
  checkInTime: '14:00',
  checkOutTime: '11:00',
  minNights: 2,
  isSuperhost: true,
  instantBook: true,
  freeCancellation: true,
  images: [
    { src: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80', alt: 'Acacia Luxury Apartments main living area with panoramic Kampala skyline views', category: 'Living Room' },
    { src: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800&q=80', alt: 'Spacious master bedroom with king-size bed and city views', category: 'Bedroom' },
    { src: 'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=800&q=80', alt: 'Modern bathroom with rainfall shower and marble finishes', category: 'Bathroom' },
    { src: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&q=80', alt: 'Rooftop swimming pool with Kampala city views at sunset', category: 'Pool' },
    { src: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800&q=80', alt: 'Fully equipped modern kitchen with premium appliances', category: 'Kitchen' },
    { src: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80', alt: 'Elegant dining area with seating for four guests', category: 'Dining' },
    { src: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800&q=80', alt: 'Private balcony with outdoor seating and garden views', category: 'Exterior' },
    { src: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&q=80', alt: 'Luxury spa and wellness centre with treatment rooms', category: 'Facilities' },
  ],
  amenities: [
    { icon: 'wifi', name: 'High-speed Wi-Fi' },
    { icon: 'pool', name: 'Rooftop swimming pool' },
    { icon: 'parking', name: 'Free secure parking' },
    { icon: 'ac', name: 'Air conditioning' },
    { icon: 'kitchen', name: 'Fully equipped kitchen' },
    { icon: 'gym', name: 'Fitness centre' },
    { icon: 'restaurant', name: 'On-site restaurant' },
    { icon: 'tv', name: 'Smart TV with Netflix' },
    { icon: 'laundry', name: 'Laundry service' },
    { icon: 'security', name: '24/7 security' },
    { icon: 'breakfast', name: 'Breakfast available' },
    { icon: 'workspace', name: 'Dedicated workspace' },
    { icon: 'default', name: 'Airport shuttle' },
    { icon: 'default', name: 'Concierge service' },
    { icon: 'default', name: 'Generator backup' },
    { icon: 'default', name: 'Pet-friendly' },
  ],
  rooms: [
    {
      id: 1,
      name: 'Deluxe Studio Apartment',
      description: 'A beautifully appointed studio with a king-size bed, modern kitchenette, and a private balcony with sweeping city views. Ideal for solo travellers or couples.',
      guests: 2,
      beds: '1 King Bed',
      size: '45 m²',
      price: 180000,
      currency: 'UGX',
      amenities: ['Free Wi-Fi', 'Air conditioning', 'Smart TV', 'Kitchenette', 'Balcony'],
      image: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600&q=80',
      imageAlt: 'Deluxe studio apartment with king-size bed and city views through large windows',
      available: true,
    },
    {
      id: 2,
      name: 'Executive One-Bedroom Suite',
      description: 'Spacious one-bedroom suite featuring a separate living area, full kitchen, and a luxurious bathroom with a soaking tub. Perfect for extended stays.',
      guests: 3,
      beds: '1 King Bed + Sofa Bed',
      size: '72 m²',
      price: 280000,
      currency: 'UGX',
      amenities: ['Free Wi-Fi', 'Air conditioning', 'Smart TV', 'Full kitchen', 'Soaking tub', 'Balcony'],
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80',
      imageAlt: 'Executive one-bedroom suite with separate living area and panoramic city views',
      available: true,
    },
    {
      id: 3,
      name: 'Premium Two-Bedroom Apartment',
      description: 'Our flagship two-bedroom apartment offers unmatched space and luxury. Two en-suite bedrooms, a full kitchen, dining area, and two private balconies.',
      guests: 4,
      beds: '2 King Beds',
      size: '110 m²',
      price: 420000,
      currency: 'UGX',
      amenities: ['Free Wi-Fi', 'Air conditioning', '2 Smart TVs', 'Full kitchen', 'Dining area', '2 Balconies', 'Laundry'],
      image: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=600&q=80',
      imageAlt: 'Premium two-bedroom apartment with spacious living area and dual balconies',
      available: true,
    },
    {
      id: 4,
      name: 'Penthouse Suite',
      description: 'The crown jewel of Acacia — a full-floor penthouse with 360° panoramic views, private rooftop terrace, chef\'s kitchen, and dedicated butler service.',
      guests: 6,
      beds: '3 King Beds',
      size: '220 m²',
      price: 850000,
      currency: 'UGX',
      amenities: ['Free Wi-Fi', 'Air conditioning', 'Private rooftop', 'Chef\'s kitchen', 'Butler service', 'Home cinema'],
      image: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=600&q=80',
      imageAlt: 'Penthouse suite with private rooftop terrace and 360-degree panoramic Kampala views',
      available: false,
    },
  ],
  host: {
    name: 'Sarah Nakato',
    avatar: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=200&q=80',
    avatarAlt: 'Sarah Nakato, host of Acacia Luxury Apartments, smiling warmly',
    joinedYear: 2019,
    reviewCount: 312,
    rating: 4.96,
    isSuperhost: true,
    responseRate: 99,
    responseTime: 'an hour',
    description: 'Welcome to Acacia! I\'m Sarah, a passionate hospitality professional born and raised in Kampala. I created Acacia Luxury Apartments to share the beauty of Uganda with travellers from around the world. My team and I are dedicated to making every stay exceptional — from arranging safari day trips to recommending the best local restaurants. We\'re always just a message away.',
    languages: ['English', 'Luganda', 'Swahili'],
  },
  reviews: [
    {
      id: 1,
      author: 'James Odhiambo',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80',
      avatarAlt: 'James Odhiambo, guest from Kenya',
      date: 'July 2025',
      rating: 5,
      comment: 'Absolutely stunning property! The views from the balcony are breathtaking and the apartment was immaculately clean. Sarah and her team went above and beyond to make us feel welcome. The rooftop pool is a highlight — we spent every evening up there watching the Kampala sunset. Will definitely be back!',
      country: 'Kenya',
    },
    {
      id: 2,
      author: 'Amira Hassan',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&q=80',
      avatarAlt: 'Amira Hassan, guest from Tanzania',
      date: 'June 2025',
      rating: 5,
      comment: 'The Executive Suite exceeded all my expectations. The kitchen was fully stocked, the bed was incredibly comfortable, and the Wi-Fi was fast enough for all my work calls. Sarah responded to every message within minutes. This is the gold standard for serviced apartments in East Africa.',
      country: 'Tanzania',
    },
    {
      id: 3,
      author: 'David Mensah',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&q=80',
      avatarAlt: 'David Mensah, guest from Ghana',
      date: 'May 2025',
      rating: 4,
      comment: 'Great location in Kololo — very safe and close to everything. The apartment was spacious and well-furnished. The only minor issue was that the gym equipment was being serviced during our stay, but the team offered us complimentary access to a nearby gym which was a nice touch.',
      country: 'Ghana',
    },
    {
      id: 4,
      author: 'Emma Richardson',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=80',
      avatarAlt: 'Emma Richardson, guest from United Kingdom',
      date: 'April 2025',
      rating: 5,
      comment: 'We stayed in the two-bedroom apartment for a week and it was perfect for our family. The kids loved the pool and we loved the restaurant downstairs. The concierge arranged a fantastic Bwindi gorilla trekking trip for us. Kampala is an amazing city and Acacia is the perfect base.',
      country: 'United Kingdom',
    },
    {
      id: 5,
      author: 'Fatou Diallo',
      avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&q=80',
      avatarAlt: 'Fatou Diallo, guest from Senegal',
      date: 'March 2025',
      rating: 5,
      comment: 'Magnifique! The apartment was beautiful, the service was impeccable, and the location is perfect. Sarah arranged airport pickup, restaurant reservations, and even a private city tour for us. I felt completely at home. Acacia is a hidden gem in Kampala.',
      country: 'Senegal',
    },
    {
      id: 6,
      author: 'Michael Otieno',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&q=80',
      avatarAlt: 'Michael Otieno, guest from Uganda',
      date: 'February 2025',
      rating: 5,
      comment: 'I\'ve stayed at many hotels in Kampala but Acacia is on a different level. The penthouse suite was absolutely spectacular — the 360° views are unmatched. Perfect for a special occasion. The butler service was discreet and professional. Highly recommended for anyone wanting the best Kampala has to offer.',
      country: 'Uganda',
    },
  ],
  ratingCategories: [
    { label: 'Cleanliness', score: 4.9 },
    { label: 'Accuracy', score: 4.8 },
    { label: 'Check-in', score: 4.9 },
    { label: 'Communication', score: 5.0 },
    { label: 'Location', score: 4.7 },
    { label: 'Value', score: 4.8 },
  ],
  bookedDates: ['2025-09-10', '2025-09-11', '2025-09-12', '2025-09-20', '2025-09-21', '2025-09-22', '2025-09-23'],
  houseRules: [
    'Check-in: 14:00 – 22:00',
    'Check-out: by 11:00',
    'No smoking inside the apartment',
    'No parties or events',
    'Pets allowed (with prior approval)',
    'Quiet hours: 22:00 – 08:00',
  ],
  cancellationPolicy: 'Free cancellation for 48 hours after booking. Cancel before check-in and get a 50% refund, minus the first night and service fee.',
};

// ─── Reserve Widget ───────────────────────────────────────────────────────────

interface ReserveWidgetProps {
  pricePerNight: number;
  currency: string;
  rating: number;
  reviewCount: number;
  checkIn: string;
  checkOut: string;
  guests: number;
  onCheckInChange: (v: string) => void;
  onCheckOutChange: (v: string) => void;
  onGuestsChange: (v: number) => void;
  onReserve: () => void;
  cleaningFee: number;
  serviceFee: number;
}

function ReserveWidget({
  pricePerNight, currency, rating, reviewCount,
  checkIn, checkOut, guests,
  onCheckInChange, onCheckOutChange, onGuestsChange,
  onReserve, cleaningFee, serviceFee,
}: ReserveWidgetProps) {
  const hasDateRange = checkIn && checkOut;

  return (
    <div className="bg-white border border-gray-200 rounded-3xl shadow-xl p-6 sticky top-24">
      {/* Price */}
      <div className="flex items-baseline gap-1.5 mb-1">
        <span className="text-2xl font-bold text-gray-900">{currency} {pricePerNight.toLocaleString()}</span>
        <span className="text-gray-500 text-sm">/ night</span>
      </div>
      <div className="flex items-center gap-1.5 mb-5">
        <svg className="w-3.5 h-3.5 text-gray-900 fill-gray-900" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
        <span className="text-sm font-semibold text-gray-900">{rating.toFixed(2)}</span>
        <span className="text-sm text-gray-500">· {reviewCount} reviews</span>
      </div>

      {/* Date + Guest inputs */}
      <div className="border border-gray-300 rounded-2xl overflow-hidden mb-3">
        <div className="grid grid-cols-2 divide-x divide-gray-300">
          <div className="p-3">
            <div className="text-xs font-bold text-gray-800 mb-1">CHECK-IN</div>
            <input
              type="date"
              value={checkIn}
              onChange={(e) => onCheckInChange(e.target.value)}
              className="text-sm text-gray-700 bg-transparent outline-none w-full cursor-pointer"
            />
          </div>
          <div className="p-3">
            <div className="text-xs font-bold text-gray-800 mb-1">CHECK-OUT</div>
            <input
              type="date"
              value={checkOut}
              onChange={(e) => onCheckOutChange(e.target.value)}
              className="text-sm text-gray-700 bg-transparent outline-none w-full cursor-pointer"
            />
          </div>
        </div>
        <div className="border-t border-gray-300 p-3">
          <div className="text-xs font-bold text-gray-800 mb-1">GUESTS</div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">{guests} guest{guests !== 1 ? 's' : ''}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onGuestsChange(Math.max(1, guests - 1))}
                className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-500 transition-colors text-gray-600"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
                </svg>
              </button>
              <span className="text-sm font-semibold text-gray-900 w-4 text-center">{guests}</span>
              <button
                onClick={() => onGuestsChange(Math.min(property.maxGuests, guests + 1))}
                className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-500 transition-colors text-gray-600"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Reserve button */}
      <button
        onClick={onReserve}
        className="w-full bg-[#4088C6] hover:bg-[#2D6FA8] text-white font-bold py-3.5 rounded-2xl transition-all duration-200 shadow-sm hover:shadow-md text-base"
      >
        Reserve
      </button>
      <p className="text-center text-xs text-gray-500 mt-2">You won't be charged yet</p>

      {/* Pricing breakdown */}
      {hasDateRange && (
        <div className="mt-5 pt-5 border-t border-gray-100">
          <PricingBreakdown
            pricePerNight={pricePerNight}
            currency={currency}
            checkIn={checkIn}
            checkOut={checkOut}
            cleaningFee={cleaningFee}
            serviceFee={serviceFee}
          />
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PropertyPage() {
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(2);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [liked, setLiked] = useState(false);

  const handleReserve = () => {
    if (!checkIn || !checkOut) {
      alert('Please select your check-in and check-out dates.');
      return;
    }
    setShowBookingModal(true);
  };

  const handleRoomSelect = (room: { name: string }) => {
    alert(`Room "${room.name}" selected! Proceeding to booking...`);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">

          {/* ── Title section ── */}
          <div className="mb-5">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{property.name}</h1>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <div className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5 text-gray-900 fill-gray-900" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="font-semibold text-gray-900">{property.rating}</span>
              </div>
              <span className="text-gray-500">·</span>
              <button className="underline font-semibold text-gray-900 hover:text-gray-600 transition-colors">
                {property.reviews.length} reviews
              </button>
              <span className="text-gray-500">·</span>
              {property.isSuperhost && (
                <>
                  <span className="font-semibold text-gray-900">Superhost</span>
                  <span className="text-gray-500">·</span>
                </>
              )}
              <div className="flex items-center gap-1 text-gray-700">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <button className="underline font-semibold text-gray-900 hover:text-gray-600 transition-colors">
                  {property.location}, {property.country}
                </button>
              </div>

              {/* Actions */}
              <div className="ml-auto flex items-center gap-2">
                <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl hover:bg-gray-100 transition-colors text-gray-700">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  <span className="text-sm font-semibold">Share</span>
                </button>
                <button
                  onClick={() => setLiked(!liked)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl hover:bg-gray-100 transition-colors text-gray-700"
                >
                  <svg
                    className={`w-4 h-4 transition-colors ${liked ? 'text-[#4088C6] fill-[#4088C6]' : ''}`}
                    fill={liked ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  <span className="text-sm font-semibold">{liked ? 'Saved' : 'Save'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* ── Gallery ── */}
          <div className="mb-8">
            <ImageGallery images={property.images} propertyName={property.name} />
          </div>

          {/* ── Two-column layout ── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

            {/* Left column */}
            <div className="lg:col-span-2 space-y-10">

              {/* Property overview */}
              <div className="pb-8 border-b border-gray-100">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-1">
                      {property.type} hosted by {property.host.name}
                    </h2>
                    <p className="text-gray-600 text-sm">
                      {property.maxGuests} guests · {property.bedrooms} bedrooms · {property.bathrooms} bathrooms
                    </p>
                  </div>
                  <div className="relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-md">
                    <img src={property.host.avatar} alt={property.host.avatarAlt} className="w-full h-full object-cover" />
                  </div>
                </div>

                {/* Highlights */}
                <div className="space-y-4">
                  {property.isSuperhost && (
                    <div className="flex items-start gap-4">
                      <svg className="w-6 h-6 text-gray-700 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{property.host.name} is a Superhost</p>
                        <p className="text-sm text-gray-500">Superhosts are experienced, highly rated hosts.</p>
                      </div>
                    </div>
                  )}
                  {property.freeCancellation && (
                    <div className="flex items-start gap-4">
                      <svg className="w-6 h-6 text-gray-700 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Free cancellation for 48 hours</p>
                        <p className="text-sm text-gray-500">Cancel within 48 hours of booking for a full refund.</p>
                      </div>
                    </div>
                  )}
                  {property.instantBook && (
                    <div className="flex items-start gap-4">
                      <svg className="w-6 h-6 text-gray-700 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Instant Book</p>
                        <p className="text-sm text-gray-500">Book without waiting for host approval.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="pb-8 border-b border-gray-100">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">{property.description}</p>
              </div>

              {/* Amenities */}
              <div className="pb-8 border-b border-gray-100">
                <Amenities amenities={property.amenities} />
              </div>

              {/* Availability Calendar */}
              <div className="pb-8 border-b border-gray-100">
                <AvailabilityCalendar
                  bookedDates={property.bookedDates}
                  checkIn={checkIn}
                  checkOut={checkOut}
                  onDateSelect={(ci, co) => { setCheckIn(ci); setCheckOut(co); }}
                />
              </div>

              {/* Rooms */}
              <div className="pb-8 border-b border-gray-100">
                <RoomsList
                  rooms={property.rooms}
                  checkIn={checkIn}
                  checkOut={checkOut}
                  onSelectRoom={handleRoomSelect}
                />
              </div>

              {/* Host Info */}
              <div className="pb-8 border-b border-gray-100">
                <HostInfo {...property.host} />
              </div>

              {/* House Rules */}
              <div className="pb-8 border-b border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-5">House rules</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {property.houseRules.map((rule, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm text-gray-700">
                      <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      {rule}
                    </div>
                  ))}
                </div>
              </div>

              {/* Cancellation Policy */}
              <div className="pb-8 border-b border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Cancellation policy</h2>
                <p className="text-gray-700 text-sm leading-relaxed">{property.cancellationPolicy}</p>
              </div>

              {/* Reviews */}
              <div className="pb-8">
                <Reviews
                  reviews={property.reviews}
                  overallRating={property.rating}
                  totalReviews={property.reviews.length}
                  categories={property.ratingCategories}
                />
              </div>
            </div>

            {/* Right column — Reserve widget */}
            <div className="lg:col-span-1">
              <ReserveWidget
                pricePerNight={property.pricePerNight}
                currency={property.currency}
                rating={property.rating}
                reviewCount={property.reviews.length}
                checkIn={checkIn}
                checkOut={checkOut}
                guests={guests}
                onCheckInChange={setCheckIn}
                onCheckOutChange={setCheckOut}
                onGuestsChange={setGuests}
                onReserve={handleReserve}
                cleaningFee={property.cleaningFee}
                serviceFee={property.serviceFee}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Mobile sticky CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 z-40 flex items-center justify-between gap-4">
        <div>
          <span className="text-base font-bold text-gray-900">{property.currency} {property.pricePerNight.toLocaleString()}</span>
          <span className="text-sm text-gray-500"> / night</span>
          {checkIn && checkOut && (
            <div className="text-xs text-gray-500 underline mt-0.5">{checkIn} – {checkOut}</div>
          )}
        </div>
        <button
          onClick={handleReserve}
          className="bg-[#4088C6] hover:bg-[#2D6FA8] text-white font-bold px-8 py-3 rounded-2xl transition-all duration-200 shadow-sm text-sm"
        >
          Reserve
        </button>
      </div>

      {/* Success toast */}
      {showBookingModal && (
        <BookingModal
          property={{
            id: property.id,
            name: property.name,
            location: property.location,
            country: property.country,
            type: property.type,
            price: property.pricePerNight,
            currency: property.currency,
            images: property.images,
            rating: property.rating,
            reviews: property.reviews,
          }}
          onClose={() => setShowBookingModal(false)}
        />
      )}

      <div className="pb-20 lg:pb-0">
        <Footer />
      </div>
    </div>
  );
}
